/**
 * Implements a generic modal as a mixin.
 *
 * For best results, mix with a Marionette.LayoutView
 * and bake for 30 minutes at 350°F
 */

import Radio from "backbone.radio";
import FooterActionsView from "../views/generic/footer-actions";
import ButtonCollectionView from "../views/generic/button/collection";

const channel = Radio.channel("global");

const CONFIG = {
  buttons: [
    {
      label: "Dismiss",
      icon: null,
      className: "btn btn-sm btn-link",
      attributes: {
        type: "reset",
        "data-dismiss": "modal",
      },
    },
  ],
};

const MODAL_OPTIONS = {
  // removes the ability to dismiss model via escape key
  keyboard: false,
  // removes the ability to dismiss model by clicking on modal backdrop
  backdrop: "static",
};

const Mixin = (superclass) =>
  class extends superclass {
    el() {
      return "#shared-modal";
    }

    template() {
      return false;
    }

    regions() {
      return {
        titleRegion: ".modal-title",
        bodyRegion: "div.modal-body",
        footerActions: "div.modal-footer div.actions",
        footerFormButtons: "div.modal-footer div.form-buttons",
      };
    }

    events() {
      return {
        "submit form": "onSubmit",
      };
    }

    initialize() {
      this.setContent();

      this.modalAPI = this.$el

        // handle model events
        // http://getbootstrap.com/javascript/#modals-events
        .on("show.bs.modal", this._onShow.bind(this))
        .on("shown.bs.modal", this._onShown.bind(this))
        .on("hide.bs.modal", this._onHide.bind(this))
        .on("hidden.bs.modal", this._onHidden.bind(this))

        // instantiate bootstrap modal
        .modal(MODAL_OPTIONS);
    }

    setContent() {
      this.titleRegion.$el.html(this.options.title || null);

      this.bodyRegion.show(
        new this.options.view({
          model: this.model || null,
        })
      );

      this.footerActions.show(new FooterActionsView());

      this.footerFormButtons.show(
        new ButtonCollectionView({
          buttons: this.options.buttons || CONFIG.buttons,
          meta: this.options,
        })
      );
    }

    unsetContent() {
      this.$(".modal-title").html("");
      this.titleRegion.empty();
      this.bodyRegion.empty();
      this.footerActions.empty();
      this.footerFormButtons.empty();
    }

    // no-op
    onSubmit(e) {
      e.preventDefault();
    }

    _onShow() {
      // no-op
    }

    _onShown() {
      // no-op
    }

    _onHide() {
      // no-op
    }

    _onHidden() {
      // remove listeners
      this.modalAPI.off();

      // remove content
      this.unsetContent();

      // redirect
      if (this.options.returnRoute) {
        if ("back" === this.options.returnRoute) {
          // request previous route
          channel.request("router:previousRoute", {
            reload: !!this.options.returnRouteRefresh,
          });
        } else {
          // request configured route
          channel.request("router:returnToRoute", this.options.returnRoute, {
            reload: !!this.options.returnRouteRefresh,
          });
          // also triggers a tab activation event
          channel.trigger("tab-activated", this.options.returnRoute);
        }
      } else {
        // redirect home
        channel.request("router:defaultRoute", {
          reload: !!this.options.returnRouteRefresh,
        });
      }
    }

    // dismisses modal by calling its API
    hide() {
      try {
        this.modalAPI.modal("hide");
      } catch (e) {
        console.warn("Unable to dismiss modal", this);
      }
    }
  };

export { Mixin as default };
